import { useFlags } from "launchdarkly-react-client-sdk";
import PropTypes from "prop-types";
import { useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Redirect } from "react-router-dom";
import { Divider, Header, Segment } from "semantic-ui-react";
import { isNil } from "../../../libs/common_utils";
import PermissionsService from "../../../services/permissions";
import useIsAdmin from "../../common/hooks/useIsAdmin";
import AppNotification from "../../common/notification-banner/app-notification";
import ProductCatalogPermissionsForm from "../components/form";

const backUrl = "/admin/product-catalog-permissions";
const permissionsService = new PermissionsService();

const ProductCatalogPermissionsEditPage = ({ match, history }) => {
  const intl = useIntl();
  const { whitelabelRoiPixelEnabled } = useFlags();
  const userIsAdmin = useIsAdmin();
  const permissionId = Number.parseInt(match.params.permissionId, 10) || 0;

  const [permission, setPermission] = useState(null);
  const [isFetchingPermission, setIsFetchingPermission] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [serverError, setServerError] = useState(null);

  const goBack = useCallback(() => {
    history.push(backUrl);
  }, [history]);

  useEffect(() => {
    if (isNil(permissionId)) return;
    const fetchPermissionData = async (id) => {
      setIsFetchingPermission(true);
      try {
        const response = await permissionsService.getPermission(id);
        setPermission(() => ({ ...response.data, advertiser_ids: response.data.advertiser_ids.map(Number) }));
      } catch (error) {
        console.error("Error fetching permission data:", error);
      } finally {
        setIsFetchingPermission(false);
      }
    };
    fetchPermissionData(permissionId);
  }, [permissionId]);

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    setServerError(null);
    try {
      await permissionsService.updatePermission(permissionId, values);
      history.push(backUrl, {
        action: "updated",
      });
    } catch (e) {
      setServerError(e.error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!(userIsAdmin || whitelabelRoiPixelEnabled)) {
    return <Redirect to="/campaigns" />;
  }

  return (
    <Segment
      basic
      style={{ padding: "0" }}
    >
      <Header as="h2">
        {intl.formatMessage({
          id: "HEADING_EDIT_PRODUCT_CATALOG_PERMISSION",
          defaultMessage: "Edit Product Catalog Permission",
        })}
      </Header>
      <Divider />
      <AppNotification
        isError
        onDismiss={() => setServerError(null)}
      >
        {serverError}
      </AppNotification>
      <Segment
        loading={isFetchingPermission}
        basic
        style={{ padding: "0" }}
      >
        {!isNil(permission) && (
          <ProductCatalogPermissionsForm
            initialData={permission}
            formType="edit"
            isSubmitting={isSubmitting}
            serverError={serverError}
            onSubmit={handleSubmit}
            onCancel={goBack}
            submitText={intl.formatMessage({
              id: "BTN_SAVE_CHANGES",
              defaultMessage: "Save Changes",
            })}
          />
        )}
      </Segment>
    </Segment>
  );
};

export default ProductCatalogPermissionsEditPage;

ProductCatalogPermissionsEditPage.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      permissionId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
};
